import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { TrendingUp, Calculator, Award } from "lucide-react";

const BreakdownSection = ({ results, loanAmount, loanTermMonths, inkindPaybackMonths }) => {
  if (!results) return null;

  const formatMoney = (value) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const netMarginPercent = (results.restaurant.netMargin * 100).toFixed(1);
  const dailyLoanCustomers = results.loan.customersNeededPerDay.toFixed(1);
  const dailyInkindCustomers = results.inkind.customersPerDay.toFixed(1);
  const extraCustomers = (results.loan.customersNeededPerDay - results.inkind.customersPerDay).toFixed(1);

  // Calculate all the totals using NET profit
  const dailyExtraRevenue = results.inkind.additionalDailyRevenue;
  const monthlyExtraRevenue = results.inkind.additionalMonthlyRevenue;
  const yearlyExtraRevenue = results.inkind.additionalYearlyRevenue;
  
  // Use NET profit
  const dailyExtraProfit = results.inkind.additionalDailyNetProfit;
  const monthlyExtraProfit = results.inkind.additionalMonthlyNetProfit;
  const yearlyExtraProfit = results.inkind.additionalYearlyNetProfit;

  return (
    <div className="space-y-6">
      <Card className="bg-[#1a1a1c] border-gray-800/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl md:text-2xl font-bold text-white tracking-wide flex items-center gap-3">
            <Calculator className="w-6 h-6 text-[#C5A572]" />
            Step 1: Customer Revenue to Financing
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-400 text-base">
            How much of your daily customer revenue goes toward paying off financing:
          </p>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-5 rounded-xl bg-red-950/30 border border-red-900/30">
              <p className="text-red-400/80 text-sm uppercase tracking-wider mb-3 font-semibold">Bank Loan</p>
              <p className="text-4xl font-bold text-white tracking-tight mb-1">
                {dailyLoanCustomers} <span className="text-lg font-normal text-gray-500">customers/day</span>
              </p>
              <p className="text-gray-500 text-sm mt-2">
                ${formatMoney(results.loan.monthlyPayment)}/month payment
              </p>
            </div>

            <div className="p-5 rounded-xl bg-[#C5A572]/5 border border-[#C5A572]/20">
              <p className="text-[#C5A572] text-sm uppercase tracking-wider mb-3 font-semibold">inKind</p>
              <p className="text-4xl font-bold text-white tracking-tight mb-1">
                {dailyInkindCustomers} <span className="text-lg font-normal text-gray-500">customers/day</span>
              </p>
              <p className="text-gray-500 text-sm mt-2">
                ${formatMoney(results.inkind.amountPerMonth)}/month (credit redemption)
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1a1a1c] border-gray-800/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl md:text-2xl font-bold text-white tracking-wide flex items-center gap-3">
            <TrendingUp className="w-6 h-6 text-[#C5A572]" />
            Step 2: Profit Retained
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-[#C5A572]/5 border border-[#C5A572]/20">
            <p className="text-[#C5A572] font-semibold text-sm mb-3">Here's how we calculate your retained profit:</p>
            <div className="space-y-2 text-sm text-gray-300">
              <p>1. Bank loan needs <span className="text-white font-medium">{dailyLoanCustomers} customers/day</span> to cover payments</p>
              <p>2. inKind only needs <span className="text-white font-medium">{dailyInkindCustomers} customers/day</span></p>
              <p>3. The difference is <span className="text-[#C5A572] font-semibold">{extraCustomers} customers/day</span> whose profit stays with you</p>
              <div className="mt-3 pt-3 border-t border-[#C5A572]/20">
                <p className="text-gray-400">
                  <span className="text-[#C5A572] font-medium">{extraCustomers} customers</span> × 
                  <span className="text-white"> ${formatMoney(results.restaurant.avgRevenuePerCustomer)}</span> (avg/customer) × 
                  <span className="text-white"> {netMarginPercent}%</span> (net margin) = 
                  <span className="text-[#C5A572] font-bold"> ${formatMoney(dailyExtraProfit)}/day</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-white/[0.02] border border-gray-800/50">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Revenue Retained</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Daily</span>
                  <span className="text-white font-mono">${formatMoney(dailyExtraRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Monthly</span>
                  <span className="text-white font-mono">${formatMoney(monthlyExtraRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Yearly</span>
                  <span className="text-white font-mono">${formatMoney(yearlyExtraRevenue)}</span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-[#C5A572]/10 border border-[#C5A572]/30">
              <p className="text-xs text-[#C5A572]/80 uppercase tracking-wider mb-3">Net Profit ({netMarginPercent}% margin)</p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Daily</span>
                  <span className="text-[#C5A572] font-mono">${formatMoney(dailyExtraProfit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Monthly</span>
                  <span className="text-[#C5A572] font-mono">${formatMoney(monthlyExtraProfit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 text-sm">Yearly</span>
                  <span className="text-[#C5A572] font-mono">${formatMoney(yearlyExtraProfit)}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-[#1a1a1c] border-gray-800/50">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl md:text-2xl font-bold text-white tracking-wide flex items-center gap-3">
            <Award className="w-6 h-6 text-[#C5A572]" />
            Your Total Additional Profit
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-5 rounded-xl bg-gradient-to-br from-[#C5A572]/20 to-[#C5A572]/5 border border-[#C5A572]/30">
              <p className="text-[#C5A572]/80 text-xs uppercase tracking-wider mb-2">Daily</p>
              <p className="text-2xl font-bold text-white tracking-tight">
                ${formatMoney(dailyExtraProfit)}
              </p>
            </div>
            <div className="p-5 rounded-xl bg-gradient-to-br from-[#C5A572]/20 to-[#C5A572]/5 border border-[#C5A572]/30">
              <p className="text-[#C5A572]/80 text-xs uppercase tracking-wider mb-2">Monthly</p>
              <p className="text-2xl font-bold text-white tracking-tight">
                ${formatMoney(monthlyExtraProfit)}
              </p>
            </div>
            <div className="p-5 rounded-xl bg-gradient-to-br from-[#C5A572]/20 to-[#C5A572]/5 border border-[#C5A572]/30">
              <p className="text-[#C5A572]/80 text-xs uppercase tracking-wider mb-2">Yearly</p>
              <p className="text-2xl font-bold text-white tracking-tight">
                ${formatMoney(yearlyExtraProfit)}
              </p>
            </div>
          </div>

          <div className="p-5 rounded-xl bg-[#C5A572]/10 border-2 border-[#C5A572]/40">
            <p className="text-[#C5A572]/80 text-sm uppercase tracking-wider mb-3 font-semibold">
              Over the {inkindPaybackMonths}-month inKind term
            </p>
            <p className="text-4xl md:text-5xl font-bold text-[#C5A572] tracking-tight">
              ${formatMoney(monthlyExtraProfit * inkindPaybackMonths)}
            </p>
            <p className="text-gray-400 text-sm mt-2">in additional profit retained</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BreakdownSection;
